import { Seo } from "@/components/Seo";

const H2 = ({ children }) => <h2 className="font-heading font-black text-xl sm:text-2xl tracking-tighter mt-10 mb-3 uppercase">{children}</h2>;
const P = ({ children }) => <p className="font-mono text-sm leading-relaxed text-ink/90 mb-3">{children}</p>;
const UL = ({ children }) => <ul className="font-mono text-sm leading-relaxed text-ink/90 mb-3 list-disc pl-6 space-y-1">{children}</ul>;

export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10" data-testid="terms-page">
      <Seo title="Terms of Service · STR Surplus" description="Terms and conditions for purchasing from STR Surplus LLC." canonical="/terms" />
      <div className="border-b-2 border-ink pb-4 mb-6">
        <div className="font-mono text-[11px] uppercase tracking-widest text-subtle">Legal</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-1">TERMS OF SERVICE</h1>
        <div className="font-mono text-xs text-subtle mt-2">Last updated: February 6, 2026 · STR Surplus LLC · Fort Lauderdale, FL 33304</div>
      </div>

      <P>These Terms of Service ("Terms") govern your use of <a href="/" className="underline">strsurplus.com</a> and any purchase you make from <strong>STR Surplus LLC</strong>, a Florida limited-liability company (FL Sunbiz Doc No. L26000449383) with its principal address in Fort Lauderdale, FL 33304 ("we", "us", "our"). By using the site or placing an order, you agree to be bound by these Terms.</P>

      <H2>1. About our products</H2>
      <P>STR Surplus sells <strong>surplus, liquidation, and auction-lot goods</strong> — new, open-box, and used items sourced from retailer overstock, returns, and STR turnovers. Every item is graded by hand and listed with:</P>
      <UL>
        <li>A condition grade (New in Box, Like New, Open Box, Used - Excellent, etc.)</li>
        <li>Photos of the actual unit or a representative photo where noted</li>
        <li>An honest description of any wear, missing pieces, or repackaging</li>
      </UL>
      <P>Colors, features, and included accessories are described as accurately as possible. However, because these are surplus goods, minor cosmetic wear consistent with the listed grade is expected and is <strong>not</strong> a defect.</P>

      <H2>2. Limited quantities · single-unit inventory</H2>
      <P>Every listing is a <strong>real, one-of-one physical unit</strong> we have on the warehouse shelf. Once it sells, that specific unit is gone. Adding an item to your cart does not reserve it until you complete checkout.</P>

      <H2>3. Pricing &amp; taxes</H2>
      <UL>
        <li>All prices are in <strong>US dollars</strong>.</li>
        <li>We reserve the right to correct pricing errors at any time before your order ships.</li>
        <li><strong>Florida sales tax</strong> is added to all orders shipped to or picked up in Florida.</li>
        <li>Bundle discounts (3+ items = 5%, 6+ items = 10%) are applied automatically at checkout.</li>
      </UL>

      <H2>4. Payment</H2>
      <P>We accept credit and debit cards via <strong>Stripe</strong>. Payment is charged immediately at checkout. We do not store your full card number.</P>

      <H2>5. Shipping &amp; local pickup</H2>
      <UL>
        <li><strong>UPS Ground</strong> to the continental US. Rates are calculated by weight and destination zone at checkout.</li>
        <li><strong>Free local pickup</strong> in Fort Lauderdale, FL. Pickup details are emailed after purchase.</li>
        <li>Orders typically ship within <strong>2 business days</strong>. We are not responsible for UPS transit delays.</li>
        <li>Risk of loss and title pass to you upon delivery to the UPS carrier.</li>
      </UL>

      <H2>6. All sales final · no returns</H2>
      <P><strong>All sales are final.</strong> Because this is liquidation inventory sold at deep discounts, we cannot accept returns, exchanges, or refund requests based on buyer's remorse, changed mind, or minor cosmetic wear consistent with the listed grade.</P>
      <P><strong>Exceptions</strong> — we will make it right if any of the following are reported to us within <strong>48 hours of delivery</strong> with photo evidence:</P>
      <UL>
        <li>The item arrives <strong>materially damaged during shipping</strong> — include photos of the box and item.</li>
        <li>The item is <strong>materially different from the listing</strong> (e.g., wrong item shipped, missing a stated accessory).</li>
        <li>The item is <strong>lost in transit</strong> per UPS tracking — once UPS confirms non-delivery.</li>
      </UL>
      <P>All exception claims must be sent to <a href="mailto:orders@strsurplus.com" className="underline">orders@strsurplus.com</a> with your order number, photos, and a brief description within the 48-hour window. Claims submitted after 48 hours cannot be honored.</P>

      <H2>7. Warranties &amp; disclaimers</H2>
      <P><strong>All items are sold "AS IS" and "WITH ALL FAULTS."</strong> To the maximum extent allowed by law, STR Surplus LLC disclaims all warranties, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement.</P>
      <P>Original manufacturer warranties may or may not apply — check the manufacturer's terms. STR Surplus does not extend, honor, or facilitate manufacturer warranty claims.</P>

      <H2>8. Limitation of liability</H2>
      <P>To the maximum extent allowed by law, STR Surplus LLC's total liability arising out of or related to any order or use of the site is limited to the <strong>amount you paid for the specific item</strong> giving rise to the claim. In no event will we be liable for indirect, incidental, special, consequential, or punitive damages, including lost profits or lost rental income.</P>

      <H2>9. Account &amp; site conduct</H2>
      <P>If you register for an account, you are responsible for keeping your password confidential. You may not:</P>
      <UL>
        <li>Use bots, scrapers, or automated tools to buy inventory.</li>
        <li>Resell our inventory in bulk without written agreement.</li>
        <li>Reverse-engineer the site or interfere with its operation.</li>
        <li>Post fraudulent reviews, chargebacks, or dispute charges without first contacting us.</li>
      </UL>
      <P>We may suspend or terminate any account that violates these Terms.</P>

      <H2>10. Reviews</H2>
      <P>Only verified buyers can leave product reviews. By posting a review, you grant STR Surplus a non-exclusive, royalty-free license to display it on the site and in marketing materials.</P>

      <H2>11. Intellectual property</H2>
      <P>All site content — logos, product photos we've taken, descriptions we've written, code, and design — is owned by STR Surplus LLC or licensed to us. You may not copy, reproduce, or repurpose site content without written permission, except that you may share individual product links on social media.</P>

      <H2>12. Privacy</H2>
      <P>Our handling of your personal information is described in our <a href="/privacy" className="underline">Privacy Policy</a>, which is incorporated into these Terms by reference.</P>

      <H2>13. Governing law &amp; dispute resolution</H2>
      <P>These Terms and any dispute arising out of them are governed by the laws of the <strong>State of Florida</strong>, without regard to conflict-of-law rules. You and STR Surplus LLC agree that any dispute will be resolved in the state or federal courts located in <strong>Broward County, Florida</strong>, and both parties consent to personal jurisdiction there.</P>

      <H2>14. Changes to these Terms</H2>
      <P>We may update these Terms from time to time. The "Last updated" date at the top reflects the latest change. Continued use of the site after changes means you accept the updated Terms.</P>

      <H2>15. Severability</H2>
      <P>If any part of these Terms is found unenforceable, the rest remains in effect.</P>

      <H2>16. Contact</H2>
      <P>
        STR Surplus LLC<br />
        Fort Lauderdale, FL 33304<br />
        FL Sunbiz Doc No. L26000449383<br />
        <a href="mailto:orders@strsurplus.com" className="underline">orders@strsurplus.com</a>
      </P>
    </div>
  );
}
